import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useTimecards, useShifts, useTriggerTimecardSync } from '../hooks/useTimecards';
import { useAllLocations } from '../hooks/useLocations';
import { useToast } from '../lib/toast';
import { classifyShiftHalf, matchShift, scheduleDiffMinutes } from '../lib/timecardHelpers';
import type { Location, TeamsTimecard, TeamsShift } from '../api/types';
import '../styles/timecards.css';

// ---------------------------------------------------------------------------
// Pay period helpers (Wed → Tue weekly)
// ---------------------------------------------------------------------------

interface PayPeriod {
  from: Date;
  to: Date;
}

function getPayPeriod(date: Date): PayPeriod {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // dayOfWeek: 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
  // Days since last Wednesday: Wed=0 Thu=1 Fri=2 Sat=3 Sun=4 Mon=5 Tue=6
  const daysFromWed = (d.getDay() + 4) % 7;
  const wed = new Date(d);
  wed.setDate(d.getDate() - daysFromWed);
  const tue = new Date(wed);
  tue.setDate(wed.getDate() + 6);
  tue.setHours(23, 59, 59, 999);
  return { from: wed, to: tue };
}

function prevPeriod(p: PayPeriod): PayPeriod {
  const d = new Date(p.from);
  d.setDate(d.getDate() - 1);
  return getPayPeriod(d);
}

function nextPeriod(p: PayPeriod): PayPeriod {
  const d = new Date(p.to);
  d.setDate(d.getDate() + 1);
  return getPayPeriod(d);
}

function fmtPeriodLabel(p: PayPeriod): string {
  const sameYear = p.from.getFullYear() === p.to.getFullYear();
  const fromStr  = p.from.toLocaleDateString([], { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
  const toStr    = p.to.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fromStr} – ${toStr}`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function fmtMinutes(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function fmtDuration(inIso: string | null | undefined, outIso: string | null | undefined): string {
  if (!inIso || !outIso) return '—';
  return fmtMinutes(Math.round((new Date(outIso).getTime() - new Date(inIso).getTime()) / 60_000));
}

function fmtDiff(mins: number | null): string {
  if (mins === null) return '—';
  if (mins === 0) return 'on time';
  return `${mins > 0 ? '+' : '−'}${fmtMinutes(Math.abs(mins))}`;
}

// Column defaults from the locations table, used if a location isn't loaded.
const DEFAULT_WINDOWS = { amShiftStart: '11:00', amShiftEnd: '17:00', pmShiftStart: '17:00', pmShiftEnd: '23:00' };

// ---------------------------------------------------------------------------
// Timecard row
// ---------------------------------------------------------------------------

interface ColumnOptions {
  showUser: boolean;
  showLocation: boolean;
  showDate: boolean;
}

function TimecardRow({
  card,
  shift,
  showLocation,
  showUser,
  showDate,
}: ColumnOptions & {
  card: TeamsTimecard;
  shift: TeamsShift | undefined;
}) {
  const name = card.displayName ?? card.entraObjectId.slice(0, 8);
  const stateBadge = {
    clockedIn:  'tc-badge tc-badge--active',
    onBreak:    'tc-badge tc-badge--break',
    clockedOut: 'tc-badge tc-badge--out',
    confirmed:  'tc-badge tc-badge--confirmed',
  }[card.state] ?? 'tc-badge';
  const diff = scheduleDiffMinutes(card, shift);
  const diffClass = diff === null || diff === 0 ? '' : diff > 0 ? ' tc-diff--over' : ' tc-diff--under';

  return (
    <tr className="tc-row">
      {showUser && (
        <td className="tc-cell tc-cell--name">
          <span className="tc-name">{name}</span>
          {!card.userId && <span className="tc-orphan-badge">unmatched</span>}
        </td>
      )}
      {showLocation && <td className="tc-cell">{card.locationName}</td>}
      {showDate && <td className="tc-cell tc-cell--date">{fmtDate(card.clockIn?.dateTime)}</td>}
      <td className="tc-cell tc-cell--time mono">{fmtTime(card.clockIn?.dateTime)}</td>
      <td className="tc-cell tc-cell--time mono">{fmtTime(card.clockOut?.dateTime)}</td>
      <td className="tc-cell tc-cell--dur mono">{fmtDuration(card.clockIn?.dateTime, card.clockOut?.dateTime)}</td>
      <td className={`tc-cell tc-cell--diff mono${diffClass}`}>{fmtDiff(diff)}</td>
      <td className="tc-cell">
        <span className={stateBadge}>{card.state.replace('clocked', '')}</span>
      </td>
      <td className="tc-cell tc-cell--shift">
        {shift
          ? <span className="tc-shift-match">{fmtTime(shift.sharedStart)} – {fmtTime(shift.sharedEnd)}</span>
          : <span className="tc-shift-none">no shift</span>}
      </td>
      {card.versionCount > 0 && (
        <td className="tc-cell tc-cell--versions">
          <span className="tc-version-badge">{card.versionCount} revision{card.versionCount > 1 ? 's' : ''}</span>
        </td>
      )}
      {card.versionCount === 0 && <td className="tc-cell" />}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function TimecardGroup({
  header,
  cards,
  shiftByCardId,
  columns,
}: {
  header: React.ReactNode;
  cards: TeamsTimecard[];
  shiftByCardId: Map<string, TeamsShift>;
  columns: ColumnOptions;
}) {
  return (
    <div className="tc-group">
      <div className="tc-group-header">
        {header}
        <span className="tc-group-count">{cards.length} punch{cards.length !== 1 ? 'es' : ''}</span>
      </div>
      {cards.length === 0 ? (
        <div className="tc-empty">No punches.</div>
      ) : (
        <table className="tc-table">
          <thead>
            <tr>
              {columns.showUser && <th>Employee</th>}
              {columns.showLocation && <th>Location</th>}
              {columns.showDate && <th>Date</th>}
              <th>In</th>
              <th>Out</th>
              <th>Duration</th>
              <th>vs. schedule</th>
              <th>Status</th>
              <th>Scheduled shift</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c) => (
              <TimecardRow key={c.id} card={c} shift={shiftByCardId.get(c.id)} {...columns} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function groupBy(cards: TeamsTimecard[], key: (c: TeamsTimecard) => string): Map<string, TeamsTimecard[]> {
  const map = new Map<string, TeamsTimecard[]>();
  for (const c of cards) {
    const k = key(c);
    const arr = map.get(k) ?? [];
    arr.push(c);
    map.set(k, arr);
  }
  return map;
}

function TimecardsTable({
  timecards,
  shifts,
  locations,
  groupByUser,
  singleDay,
}: {
  timecards: TeamsTimecard[];
  shifts: TeamsShift[];
  locations: Location[];
  groupByUser: boolean;
  singleDay: boolean;
}) {
  const shiftByCardId = useMemo(() => {
    const map = new Map<string, TeamsShift>();
    for (const c of timecards) {
      const s = matchShift(c, shifts);
      if (s) map.set(c.id, s);
    }
    return map;
  }, [timecards, shifts]);

  if (timecards.length === 0) {
    return <div className="tc-empty">No timecards found for this period.</div>;
  }

  if (singleDay) {
    const windowsById = new Map(locations.map((l) => [l.id, l]));
    const byHalf = groupBy(timecards, (c) => classifyShiftHalf(c, windowsById.get(c.locationId) ?? DEFAULT_WINDOWS));
    const columns = {
      showUser: true,
      showLocation: new Set(timecards.map((c) => c.locationId)).size > 1,
      showDate: false,
    };
    return (
      <div className="tc-groups">
        <TimecardGroup
          header={<span className="tc-group-name">AM bartenders</span>}
          cards={byHalf.get('am') ?? []}
          shiftByCardId={shiftByCardId}
          columns={columns}
        />
        <TimecardGroup
          header={<span className="tc-group-name">PM bartenders</span>}
          cards={byHalf.get('pm') ?? []}
          shiftByCardId={shiftByCardId}
          columns={columns}
        />
      </div>
    );
  }

  if (groupByUser) {
    return (
      <div className="tc-groups">
        {[...groupBy(timecards, (c) => c.userId ?? c.entraObjectId).entries()].map(([key, cards]) => {
          const first = cards[0];
          if (!first) return null;
          return (
            <TimecardGroup
              key={key}
              header={
                <>
                  <span className="tc-group-name">{first.displayName ?? first.entraObjectId.slice(0, 8)}</span>
                  {!first.userId && <span className="tc-orphan-badge">unmatched</span>}
                </>
              }
              cards={cards}
              shiftByCardId={shiftByCardId}
              columns={{ showUser: false, showLocation: true, showDate: true }}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="tc-groups">
      {[...groupBy(timecards, (c) => c.locationId).entries()].map(([locId, cards]) => {
        const first = cards[0];
        if (!first) return null;
        return (
          <TimecardGroup
            key={locId}
            header={<span className="tc-group-name">{first.locationName}</span>}
            cards={cards}
            shiftByCardId={shiftByCardId}
            columns={{ showUser: true, showLocation: false, showDate: true }}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ViewMode = 'user' | 'location';

export function TimecardsPage(): React.ReactElement {
  const { toast } = useToast();
  const { data: locData } = useAllLocations();
  const locations = locData?.items ?? [];

  // Period state
  const [period, setPeriod]         = useState<PayPeriod>(() => getPayPeriod(new Date()));
  const [singleDay, setSingleDay]   = useState(false);
  const [selectedDay, setSelectedDay] = useState(() => new Date().toISOString().slice(0, 10));

  // View controls
  const [viewMode, setViewMode]     = useState<ViewMode>('location');
  const [locationFilter, setLocationFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const effectivePeriod: PayPeriod = singleDay
    ? (() => {
        const d = new Date(selectedDay + 'T00:00:00');
        const end = new Date(d); end.setHours(23, 59, 59, 999);
        return { from: d, to: end };
      })()
    : period;

  const tcParams = {
    from: effectivePeriod.from.toISOString(),
    to:   effectivePeriod.to.toISOString(),
    ...(locationFilter ? { locationId: locationFilter } : {}),
  };

  const { data: tcData, isLoading: tcLoading, error: tcError } = useTimecards(tcParams);
  const { data: shData, isLoading: shLoading }                 = useShifts(tcParams);

  const syncMutation = useTriggerTimecardSync();

  const allTimecards = useMemo(() => tcData?.items ?? [], [tcData]);
  const allShifts    = useMemo(() => shData?.items ?? [], [shData]);

  // Filter by user search
  const filteredTimecards = useMemo(() => {
    if (!userSearch.trim() || viewMode !== 'user') return allTimecards;
    const q = userSearch.toLowerCase();
    return allTimecards.filter(
      (c) => c.displayName?.toLowerCase().includes(q) || c.entraObjectId.toLowerCase().includes(q),
    );
  }, [allTimecards, userSearch, viewMode]);

  async function handleSync() {
    try {
      const result = await syncMutation.mutateAsync();
      const counts = `${result.timecardsUpserted} timecards, ${result.shiftsUpserted} shifts updated`;
      if (result.warnings.length > 0) {
        toast.error(`Sync finished with ${result.warnings.length} warning(s) (${counts}): ${result.warnings[0]}`);
      } else {
        toast.success(`Synced: ${counts}`);
      }
    } catch {
      toast.error('Sync failed — check the admin panel for details');
    }
  }

  const isLoading = tcLoading || shLoading;

  return (
    <div className="canto-page">
      {/* Header */}
      <div className="canto-page-header">
        <div>
          <h1>Timecards</h1>
          <div className="page-meta">Teams Shifts · Admin &amp; Payroll</div>
        </div>
        <button
          className="btn btn-sm btn-ghost"
          onClick={handleSync}
          disabled={syncMutation.isPending}
          title="Re-sync all locations from Teams"
        >
          <RefreshCw size={14} style={{ marginRight: 6 }} />
          {syncMutation.isPending ? 'Syncing…' : 'Sync now'}
        </button>
      </div>

      {/* Controls bar */}
      <div className="tc-controls">
        {/* Period navigator */}
        <div className="tc-period-nav">
          {!singleDay && (
            <>
              <button className="icon-btn" onClick={() => setPeriod(prevPeriod(period))} aria-label="Previous period">
                <ChevronLeft size={16} />
              </button>
              <span className="tc-period-label">{fmtPeriodLabel(period)}</span>
              <button
                className="icon-btn"
                onClick={() => setPeriod(nextPeriod(period))}
                disabled={nextPeriod(period).from > new Date()}
                aria-label="Next period"
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}
          {singleDay && (
            <input
              type="date"
              className="tc-date-input"
              value={selectedDay}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setSelectedDay(e.target.value)}
            />
          )}
          <div className="seg-ctrl">
            <button className={`seg-btn${!singleDay ? ' active' : ''}`} onClick={() => setSingleDay(false)}>
              Pay period
            </button>
            <button className={`seg-btn${singleDay ? ' active' : ''}`} onClick={() => setSingleDay(true)}>
              Single day
            </button>
          </div>
        </div>

        {/* View mode + filters */}
        <div className="tc-filters">
          <div className="seg-ctrl">
            <button
              className={`seg-btn${viewMode === 'location' ? ' active' : ''}`}
              onClick={() => setViewMode('location')}
            >
              By location
            </button>
            <button
              className={`seg-btn${viewMode === 'user' ? ' active' : ''}`}
              onClick={() => setViewMode('user')}
            >
              By employee
            </button>
          </div>

          {locations.length > 0 && (
            <select
              className="tc-select"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}

          {viewMode === 'user' && (
            <input
              type="search"
              placeholder="Search employee…"
              className="tc-search"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          )}
        </div>
      </div>

      {/* Period label (single day) */}
      {singleDay && (
        <div className="tc-day-label">
          {new Date(selectedDay + 'T00:00:00').toLocaleDateString([], {
            weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
          })}
        </div>
      )}

      {/* Content */}
      {isLoading && <div className="tc-loading">Loading timecards…</div>}
      {!isLoading && tcError && (
        <div className="tc-error">Failed to load timecards. {(tcError as Error).message}</div>
      )}
      {!isLoading && !tcError && (
        <TimecardsTable
          timecards={filteredTimecards}
          shifts={allShifts}
          locations={locations}
          groupByUser={viewMode === 'user'}
          singleDay={singleDay}
        />
      )}
    </div>
  );
}
